import {
  DatePicker,
  type DatePickerProps,
  type PickerValidDate,
} from '@mui/x-date-pickers-pro'
import dayjs, { type Dayjs } from 'dayjs'
import { get, type PropertyPath, set } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useSearch } from '../../hooks/useSearch'
import { useAppSelector } from '@/redux/hooks'
import type { PropertyStringPath, SearchFilterState } from '../../types'

type Props = {
  label: string
  fieldName: string
  fieldPath: PropertyStringPath<SearchFilterState> & PropertyPath
} & DatePickerProps

export const FilterDate = (props: Props) => {
  const { fieldPath, fieldName, ...rest } = props
  const { selectors, updateNext } = useSearch()

  const nextSearchOptions = useAppSelector(selectors.selectNextSearchParameters)

  const value: PickerValidDate | null =
    get(nextSearchOptions, fieldPath) || null

  const formatLocalValue = useCallback(
    (value: Dayjs | null) => (value ? dayjs(value) : null),
    [],
  )

  const [localValue, setLocalValue] = useState<Dayjs | null>(
    formatLocalValue(value),
  )

  const updateState = (value: Dayjs | null) => {
    if (value?.isValid() || value === null) {
      const newValue = value ? value.toDate() : null
      updateNext(set({}, fieldPath, newValue))
    }
  }

  const handleChange: DatePickerProps['onChange'] = (value) => {
    setLocalValue(value)
  }

  const handleAccept: DatePickerProps['onAccept'] = (value) => {
    updateState(value)
  }

  const handleClear = () => {
    setLocalValue(null)
    updateState(null)
  }

  useEffect(() => {
    setLocalValue(formatLocalValue(value))
  }, [value, formatLocalValue])

  return (
    <DatePicker
      slotProps={{
        field: { clearable: true, onClear: handleClear },
        textField: { fullWidth: true },
      }}
      onAccept={handleAccept}
      onChange={handleChange}
      {...rest}
      name={fieldName}
      value={localValue}
      views={['year', 'month', 'day']}
      maxDate={dayjs()}
    />
  )
}
